/** @format — Unified search field (documents, chats, shipments) */

import { useState } from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

/** Matches filter control height on desktop */
export const SEARCH_CONTROL_H = "h-9 min-h-[36px] max-h-9";

const HEIGHT = {
  sm: SEARCH_CONTROL_H,
  md: "h-[38px] min-h-[38px] max-h-[38px]",
  /** Documents toolbar: h-9 mobile, h-7 desktop */
  docs: "h-9 min-h-9 max-h-9 md:h-7 md:min-h-7 md:max-h-7",
};

export default function SearchField({
  value,
  onChange,
  placeholder = "Search",
  size = "sm",
  className = "",
  inputClassName = "",
  clearable = false,
  onClear,
  ...inputProps
}: {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  size?: "sm" | "md" | "docs";
  className?: string;
  inputClassName?: string;
  clearable?: boolean;
  onClear?: () => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "size">) {
  const [focused, setFocused] = useState(false);
  const showClear = clearable && Boolean(value);

  return (
    <div
      className={cn(
        "box-border flex min-w-0 items-center gap-1 overflow-hidden rounded-lg border bg-white px-1.5 shadow-none transition-colors",
        HEIGHT[size] ?? HEIGHT.sm,
        size === "sm" || size === "md" ? "gap-2 px-3" : "",
        focused
          ? "border-[#2563EB] shadow-[0_0_0_3px_rgba(37,99,235,0.1)]"
          : "border-neutral-200",
        className,
      )}
    >
      <Search
        className={cn(
          "shrink-0 text-[#737373]",
          size === "docs" ? "h-4 w-4" : "h-3.5 w-3.5",
        )}
        strokeWidth={size === "docs" ? 2 : 1.75}
        aria-hidden
      />
      <input
        type="text"
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        className={cn(
          "h-full min-w-0 flex-1 border-0 bg-transparent p-0 outline-none ring-0 placeholder:text-neutral-500 focus:border-0 focus:outline-none focus:ring-0",
          size === "docs" ? "text-xs leading-none text-[#0a0a0a]" : "text-[13px] text-[#344054] placeholder-[#98A2B3]",
          inputClassName,
        )}
        {...inputProps}
      />
      {showClear ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            onClear?.();
            onChange({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>);
          }}
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
        >
          <X className="h-3 w-3" strokeWidth={2} />
        </button>
      ) : null}
    </div>
  );
}
